/**
 * The shared control vocabulary, built on Base UI primitives.
 *
 * Base UI supplies behaviour and accessibility; every visual decision is a
 * Tailwind class here, so there is one place to change how a button or a
 * dialog looks across the whole app.
 */
import { Dialog } from '@base-ui/react/dialog';
import { AlertDialog } from '@base-ui/react/alert-dialog';
import { Select as BaseSelect } from '@base-ui/react/select';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { Menu } from '@base-ui/react/menu';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand text-brand-ink hover:opacity-90 border-transparent',
  secondary: 'bg-surface text-ink border-line hover:bg-surface-2',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-surface-2',
  danger: 'bg-danger text-white border-transparent hover:opacity-90',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  /** Square, icon-only. Still needs an aria-label. */
  icon?: boolean;
}

export function Button({
  variant = 'secondary',
  loading = false,
  icon = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg border text-sm font-medium',
        'transition-colors disabled:opacity-50 disabled:pointer-events-none',
        // 40px minimum: a comfortable touch target without being oversized on
        // a desktop toolbar.
        icon ? 'size-10 shrink-0' : 'h-10 px-3.5',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

const FIELD_CLASS =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-dim/70 focus:outline-none focus-visible:outline-2 ' +
  'focus-visible:outline-brand focus-visible:outline-offset-1';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(FIELD_CLASS, 'h-10', className)} {...rest} />;
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(FIELD_CLASS, 'min-h-20 resize-y', className)} {...rest} />;
}

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-ink-dim">
      {children}
    </label>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  indeterminate,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label?: ReactNode;
  indeterminate?: boolean;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm select-none">
      <BaseCheckbox.Root
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={onCheckedChange}
        className={cx(
          'flex size-5 shrink-0 items-center justify-center rounded border transition-colors',
          checked || indeterminate ? 'border-brand bg-brand' : 'border-line bg-surface',
        )}
      >
        <BaseCheckbox.Indicator className="text-brand-ink">
          {indeterminate ? (
            <span className="block h-0.5 w-2.5 rounded bg-current" />
          ) : (
            <Check className="size-3.5" strokeWidth={3} />
          )}
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      {label}
    </label>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: readonly SelectOption<T>[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <BaseSelect.Root
      value={value}
      onValueChange={(next) => onChange(next as T)}
      items={options as never}
    >
      <BaseSelect.Trigger
        aria-label={ariaLabel}
        className={cx(
          'inline-flex h-10 items-center justify-between gap-2 rounded-lg border border-line',
          'bg-surface px-3 text-sm text-ink hover:bg-surface-2',
          className,
        )}
      >
        <BaseSelect.Value />
        <ChevronDown className="size-4 text-ink-dim" aria-hidden />
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={6} className="z-50">
          <BaseSelect.Popup className="min-w-40 rounded-xl border border-line bg-surface p-1 shadow-lg">
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                className={cx(
                  'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm',
                  'data-[highlighted]:bg-surface-2',
                )}
              >
                <BaseSelect.ItemIndicator className="text-brand">
                  <Check className="size-4" />
                </BaseSelect.ItemIndicator>
                <BaseSelect.ItemText className="data-[selected]:font-medium">
                  {option.label}
                </BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

const BACKDROP =
  'fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] transition-opacity ' +
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0';

/**
 * Full-screen from the bottom on a phone, a centred card on a desktop. The
 * same component either way, so a dialog never has a second mobile
 * implementation to keep in step.
 */
const POPUP =
  'fixed z-50 flex flex-col bg-surface text-ink shadow-2xl transition-all ' +
  'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl ' +
  'data-[starting-style]:translate-y-4 data-[ending-style]:translate-y-4 ' +
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 ' +
  'sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[85dvh] sm:w-[min(34rem,92vw)] ' +
  'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-line ' +
  'sm:data-[starting-style]:translate-y-[calc(-50%+1rem)] ' +
  'sm:data-[ending-style]:translate-y-[calc(-50%+1rem)]';

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className={BACKDROP} />
        <Dialog.Popup className={cx(POPUP, wide && 'sm:w-[min(56rem,94vw)]')}>
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-sm text-ink-dim">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="-mt-1 -mr-1 rounded-lg p-2 text-ink-dim hover:bg-surface-2"
            >
              <X className="size-5" />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Confirmation
//
// One styled dialog for every destructive action rather than window.confirm,
// so the wording can say what will actually happen and the confirm button can
// be styled destructively. Focus starts on Cancel, so a stray Enter cannot
// delete anything.
// ---------------------------------------------------------------------------

interface ConfirmRequest {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
}

type Resolver = (confirmed: boolean) => void;

const ConfirmContext = createContext<((request: ConfirmRequest) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [resolver, setResolver] = useState<{ resolve: Resolver } | null>(null);

  const confirm = useCallback((next: ConfirmRequest) => {
    setRequest(next);
    return new Promise<boolean>((resolve) => setResolver({ resolve }));
  }, []);

  // Cancel, Escape and a backdrop click all count as a decision. If none of
  // them settled the promise the caller would wait forever and the action
  // would look stuck.
  const settle = useCallback(
    (confirmed: boolean) => {
      resolver?.resolve(confirmed);
      setResolver(null);
      setRequest(null);
    },
    [resolver],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog.Root
        open={request !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Backdrop className={BACKDROP} />
          <AlertDialog.Popup className={POPUP}>
            <div className="px-5 pt-5">
              <AlertDialog.Title className="text-base font-semibold">
                {request?.title}
              </AlertDialog.Title>
              <AlertDialog.Description className="mt-2 text-sm text-ink-dim">
                {request?.body}
              </AlertDialog.Description>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button autoFocus onClick={() => settle(false)}>
                Cancel
              </Button>
              <Button
                variant={request?.destructive === false ? 'primary' : 'danger'}
                onClick={() => settle(true)}
              >
                {request?.confirmLabel ?? 'Delete'}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside ConfirmProvider');
  return confirm;
}

// ---------------------------------------------------------------------------
// Menus
// ---------------------------------------------------------------------------

export function DropdownMenu({
  trigger,
  children,
  ariaLabel,
}: {
  trigger: ReactNode;
  children: ReactNode;
  ariaLabel: string;
}) {
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={ariaLabel}
        render={<span className="inline-flex" />}
        nativeButton={false}
      >
        {trigger}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="end" className="z-50">
          <Menu.Popup className="min-w-48 rounded-xl border border-line bg-surface p-1 shadow-lg">
            {children}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

export function MenuAction({
  children,
  onClick,
  destructive,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <Menu.Item
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm',
        'data-[highlighted]:bg-surface-2 data-[disabled]:opacity-40 data-[disabled]:pointer-events-none',
        destructive && 'text-danger',
      )}
    >
      {children}
    </Menu.Item>
  );
}

export function MenuDivider() {
  return <Menu.Separator className="my-1 h-px bg-line" />;
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-sm text-ink-dim">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label ?? 'Loading…'}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon ? <div className="text-ink-dim/60">{icon}</div> : null}
      <div>
        <p className="font-medium">{title}</p>
        {body ? <p className="mt-1 text-sm text-ink-dim">{body}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Pill({ children, tone }: { children: ReactNode; tone?: 'brand' | 'warn' | 'dim' }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        tone === 'brand' && 'bg-brand/15 text-brand',
        tone === 'warn' && 'bg-warn/20 text-warn',
        (tone === 'dim' || !tone) && 'bg-surface-2 text-ink-dim',
      )}
    >
      {children}
    </span>
  );
}

/** Page-level pagination, shared by every list. */
export function Pager({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (next: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const shown = useMemo(() => ({ from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total) }), [page, pageSize, total]);
  if (total <= pageSize) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 text-sm text-ink-dim">
      <span>
        {shown.from}–{shown.to} of {total}
      </span>
      <div className="flex gap-2">
        <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
